import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { ListPage } from 'app/design/primitives/page/list';
import { Text } from 'app/design/primitives/text';
import { ClubName } from 'app/components/club-name';
import type { Address } from 'app/types/address';
import type { Group } from 'app/types/group';
import type { SpecificNavigationProp } from 'app/types/navigation';
import MemberCard from 'app/screens/groups/member-card';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { useFindUsers, type FoundUser } from 'app/network/user';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { localeAwareFormatDistance } from 'app/lib/date';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, useWindowDimensions } from 'react-native';
import { useUISize } from 'app/utils/web-helpers';

type PeopleResultsLayout = 'row' | 'card';

interface SearchPeopleResultsProps {
  addresses?: Address[];
  groups?: Group[];
  isLoggedIn?: boolean;
  layout?: PeopleResultsLayout;
  navigation: SpecificNavigationProp<'all-listings'>;
  search?: string;
  selectedAddresses?: string[];
  selectedGroups?: string[];
  canSearchPeople?: boolean;
  peopleQuery?: ReturnType<typeof useFindUsers>;
  showGroups?: boolean;
}

const getCardColumns = (width: number) => {
  if (width >= 1280) return 4;
  if (width >= 960) return 3;
  if (width >= 640) return 2;
  return 1;
};

const PeopleCardSeparator = () => <Column marginTop="spacing.general.x4" />;

export function SearchPeopleResults({
  addresses,
  groups,
  isLoggedIn = false,
  layout = 'row',
  navigation,
  search = '',
  selectedAddresses = [],
  selectedGroups = [],
  canSearchPeople: explicitCanSearchPeople,
  peopleQuery: externalPeopleQuery,
  showGroups: explicitShowGroups,
}: SearchPeopleResultsProps) {
  const { t } = useTranslation('all-listings');
  const { width } = useWindowDimensions();
  const normalizedSearch = search.trim();
  const { isSmallUI } = useUISize();

  const { peopleQuery, canSearchPeople } = usePeopleSearchQuery(
    selectedGroups,
    groups,
    selectedAddresses,
    addresses,
    isLoggedIn,
    normalizedSearch,
    externalPeopleQuery,
    explicitCanSearchPeople,
  );

  const people = peopleQuery.data?.pages.flatMap((page) => page.results) ?? [];
  const total = peopleQuery.data?.pages[0]?.total ?? 0;
  const showGroups = explicitShowGroups ?? selectedGroups.length === 0;
  const cardColumns = layout === 'card' ? getCardColumns(width) : undefined;

  if (!canSearchPeople) {
    return null;
  }

  return (
    <Column flex={1} minHeight={0}>
      {!peopleQuery.isPending && isSmallUI ? (
        <Row marginVertical="spacing.general.x2" paddingHorizontal="spacing.general.x4">
          <Text color="text.secondary" fontSize={13}>
            {t('peopleSearch.subtitle', { count: total })}
          </Text>
        </Row>
      ) : null}

      {peopleQuery.isPending ? (
        <Column alignItems="center" paddingVertical="spacing.general.x4">
          <ActivityIndicator />
        </Column>
      ) : people.length === 0 ? (
        <Column alignItems="center" paddingHorizontal="spacing.general.x4" paddingVertical="spacing.general.x8">
          <Text color="text.secondary" textAlign="center">
            {t('peopleSearch.empty')}
          </Text>
        </Column>
      ) : (
        <ListPage
          data={people}
          contentContainerStyle={{
            paddingBottom: 120,
          }}
          numColumns={cardColumns}
          ItemSeparatorComponent={layout === 'card' ? PeopleCardSeparator : undefined}
          keyExtractor={(person) => person.id}
          onEndReached={() => {
            if (peopleQuery.hasNextPage && !peopleQuery.isFetchingNextPage) {
              void peopleQuery.fetchNextPage();
            }
          }}
          onEndReachedThreshold={0.4}
          refreshing={peopleQuery.isRefetching}
          renderItem={({ item: person }) => (
            <Column
              paddingHorizontal={layout === 'card' ? undefined : 'spacing.general.x4'}
              paddingBottom="spacing.general.x2"
            >
              <Pressable onPress={() => navigateToUserProfile(navigation, person.id)}>
                {layout === 'card' ? (
                  <SearchPersonCard person={person} showGroups={showGroups} />
                ) : (
                  <MemberCard {...person} showGroups={showGroups} />
                )}
              </Pressable>
            </Column>
          )}
          withPadding={layout === 'card'}
        />
      )}
    </Column>
  );
}

const SEARCH_PERSON_CARD_WIDTH = 200;
const SEARCH_PERSON_CARD_HEIGHT = 220;

function usePeopleSearchQuery(
  selectedGroups: string[],
  groups: Group[] | undefined,
  selectedAddresses: string[],
  addresses: Address[] | undefined,
  isLoggedIn: boolean,
  normalizedSearch: string,
  externalPeopleQuery: ReturnType<typeof useFindUsers> | undefined,
  explicitCanSearchPeople: boolean | undefined,
) {
  const groupIds = selectedGroups.length > 0 ? selectedGroups : (groups?.map((group) => group.id) ?? []);
  const addressIds = selectedAddresses.length > 0 ? selectedAddresses : (addresses?.map((address) => address.id) ?? []);

  const computedCanSearchPeople =
    isLoggedIn && normalizedSearch.length >= 1 && (groupIds.length > 0 || addressIds.length > 0);
  const localPeopleQuery = useFindUsers(
    {
      addressIds,
      groupIds,
      search: normalizedSearch,
      sortBy: 'relevance',
    },
    { enabled: externalPeopleQuery === undefined && computedCanSearchPeople },
  );
  const canSearchPeople = explicitCanSearchPeople ?? computedCanSearchPeople;
  const peopleQuery = externalPeopleQuery ?? localPeopleQuery;
  return { peopleQuery, canSearchPeople };
}

function SearchPersonCard({ person, showGroups }: { person: FoundUser; showGroups: boolean }) {
  const { t } = useTranslation();
  const { convertDistance } = useDistanceUnit();
  const fullName = [person.profile?.firstName, person.profile?.lastName].filter(Boolean).join(' ');
  const initials = [person.profile?.firstName[0], person.profile?.lastName[0]].filter(Boolean).join('');
  const groups =
    person.memberships?.map((membership) => ({
      id: (membership.groupId ?? membership.group.id)!,
      name: membership.group.name,
    })) ?? [];

  return (
    <Column
      alignItems="center"
      backgroundColor="surface.primary-neutral"
      borderRadius="radius.lg"
      gap="spacing.general.x2"
      height={SEARCH_PERSON_CARD_HEIGHT}
      borderColor="surface.tertiary-neutral"
      borderWidth={1}
      padding="spacing.general.x4"
      width={SEARCH_PERSON_CARD_WIDTH}
    >
      {person.profile?.picture ? (
        <PCImage
          aspectRatio={1}
          cachePolicy="memory-disk"
          height={96}
          recyclingKey={`${person.profile.picture}-member-card-large`}
          source={{
            uri: getImageVariantUrl(person.profile.picture, 'medium'),
          }}
          style={{ borderRadius: 48 }}
        />
      ) : (
        <Column
          alignItems="center"
          backgroundColor="surface.tertiary-neutral"
          height={96}
          justifyContent="center"
          width={96}
          style={{ borderRadius: 48 }}
        >
          <Text color="text.tertiary" fontSize={32} fontWeight="700">
            {initials}
          </Text>
        </Column>
      )}

      <Column alignItems="center" gap="spacing.general.x0,5" width="100%">
        <Text fontWeight="700" numberOfLines={1} textAlign="center" variant="bodyBase">
          {fullName}
        </Text>

        {person.addresses?.[0] ? (
          <Row gap="spacing.general.x0,5" justifyContent="center" width="100%">
            <Text color="text.secondary" flexShrink={1} numberOfLines={1} textAlign="center" variant="bodyBase">
              {person.addresses[0].city}
            </Text>
            {person.distance ? (
              <Text color="text.secondary" numberOfLines={1} variant="bodyBase">
                -
              </Text>
            ) : null}
            {person.distance ? (
              <Text color="text.secondary" fontWeight="700" numberOfLines={1} variant="bodyBase">
                {convertDistance(person.distance, { alreadyInKm: true, withUnit: true })}
              </Text>
            ) : null}
          </Row>
        ) : null}

        <SearchPersonActivity person={person} t={t} />
      </Column>

      {showGroups ? <ClubName groups={groups} preventNavigateToClubMembers smaller /> : null}
    </Column>
  );
}

function SearchPersonActivity({ person, t }: { person: FoundUser; t: ReturnType<typeof useTranslation>['t'] }) {
  return person.trustPoints || person.lastSeenAt ? (
    <Text color="text.tertiary" numberOfLines={1} textAlign="center" variant="bodyBase">
      {person.trustPoints ? `👍 (${person.trustPoints.toFixed(0)})` : ''}
      {person.trustPoints && person.lastSeenAt ? ' | ' : ''}
      {!person.lastSeenAt
        ? ''
        : t('lastActiveAgo', {
            ago: localeAwareFormatDistance(new Date(), new Date(person.lastSeenAt)),
            ns: 'common',
          })}
    </Text>
  ) : null;
}
